/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import {tableButtonGray} from "../../../constants/colors";
import {setGlobalDialogComponent, setIsGlobalDialogOpen} from "../../../redux/ducks/GlobalDialog/slice";
import {setGlobalRightClickMenuData, setGlobalRightClickMenuPosition} from "../../../redux/ducks/GlobalRightClickMenu/slice";
import IconButton from "@mui/material/IconButton";
import {ArgosLinkIcon} from "../../components/Icons/ArgosActionIcons";
import {IComponent} from "../../components/Global/Dialog/GlobalDialog";
import {CAdasProgramsRequests} from "../AdasPrograms/ItemDetails/types";
import {Delete as DeleteIcon, PageviewOutlined as ViewDetailsIcon} from '@mui/icons-material';
import React from "react";


const objectDefinitionsTitle = "Object Definitions Database"
const potentiallyMovingObjectsTitle = "Potentially Moving Objects"
const objectClassificationTitle = "Object Classification"
const picklistDataTitle = "Sign Objects - Picklist Data"
const signObjectsTitle = "Sign Objects"
const miscellaneousObjectsTitle = "Miscellaneous Objects"
const objectDetails = "/objectDetails"
const versionsTabUrlParam = "id"
const fetchObjectsEndpoint = "/HttpTriggerGetDataCoreSQL?container=Objects"
const fetchRealWorldObjectsEndpoint = "/HttpTriggerGetDataCoreSQL?container=RealWorldObjects"
const fetchRealWorldObjectsPdfEndpoint = "/HttpTriggerDownloadFile?request=RWOPdf"
const addRealWorldObjectImageEndpoint = "/HttpTriggerUploadImage"
const fetchJamaMetadataEndpoint = "/HttpTriggerGetDataCoreSQL?container=JamaMetadata"
const deleteObjectsEndpoint = "/HttpTriggerDeleteData?container=Objects"
const reloadObjectsEndpoint = CAdasProgramsRequests.updateOrFetchArgosJamaTreeData.getUrl(CAdasProgramsRequests.updateOrFetchArgosJamaTreeData.actionUrlParams.loadJamaObjects)
const addObjectEndpoint = "/HttpTriggerAddObject"
const updateObjectEndpoint = "/HttpTriggerAddObject?update=true"
const generateUrlEndpoint = "/HttpTriggerGenerateLink"
const savePicklistEndpoint = "/HttpTriggerAddObject?action=updatePickList"

interface Link {
  name: string
  url: string
}

interface Table {
  property: string
  unit: string
  value: any
  note: string
}

export interface Note {
  note: string
  url: string
}

export interface ObjectDef {
  id: string
  type: string
  name: string
  version: string
  widthHorizontal: number
  heightVertical: number
  length: number
  oWidth: number
  oHeight: number
  rcs: number
  retroreflectivity: number
  image: string
  pixelsAcrossShortDimension: number
  pointsOnObject: number
  links: Link[]
  table: Table[]
  notes: Note
  dateCreated: string
  globalId: string
  batch: string
}

export const componentNameToDialogComponentMap = {
  ObjectDetails: "ObjectItemDetails",
  SignsRWO: "SignsRWODetails",
  PotentiallyMovingObjects: "PotentiallyMovingObjectDetails",
  MiscellaneousObjects: "MiscellaneousObjectsDetails"
}

export interface ObjectRow {
  id: string
  type: string
  name: string
  widthHorizontal: number
  heightVertical: number
  length: number
  rcs: number
  retroreflectivity: number
  image: string
  oWidth: number
  oHeight: number
  //pointsOnObject: number
  //pixelsAcrossShortDimension: number
  latestVersion: string
  timestamp: string
  links: Link[]
  //table: Table[]
}

export interface RealWorldObjectDef {
  type: any;
  parameters: any;
  images: any;
  image: any;
  id: string,
  objectName: string,
  minimumWidth: string,
  maximumWidth: string,
  minimumHeight: string,
  maximumHeight: string,
  minimumLength: string,
  maximumLength: string,
  description: string,
  fileName: string,
  objectClassification: any,
  wheelbase: string,
  comments: string,
  latestVersion: string,
  lastUpdated: string,
  table: Table[],
  objectRequestSource: string,
  references: string,
}

export interface RealWorldObjectRow {
  id: string
  objectName: string,
  minimumWidth: string,
  maximumWidth: string,
  minimumHeight: string,
  maximumHeight: string,
  minimumLength: string,
  maximumLength: string,
  description: string,
  fileName: string,
  objectClassification: string,
  wheelbase: string,
  comments: string,
  latestVersion: string,
  lastUpdated: string
}

export const emptyObject: ObjectDef = {
  id: "",
  image: "",
  type: "",
  name: "",
  version: "",
  widthHorizontal: NaN,
  length: NaN,
  oWidth: NaN,
  oHeight: NaN,
  heightVertical: NaN,
  rcs: NaN,
  retroreflectivity: NaN,
  pointsOnObject: NaN,
  pixelsAcrossShortDimension: NaN,
  links: [],
  table: [/* {
      Property: "Length",
      Unit: "m",
      Value: NaN,
      note: ""
  } */],
  notes: {
    note: "",
    url: ""
  },
  dateCreated: "",
  globalId: "",
  batch: ""
}


/**
 * Generates a set of actions for a table row, including view details, open links, and delete object.
 *
 * @param {any} row - The data for the current row.
 * @param {any} dispatch - The dispatch function to trigger actions.
 * @param {string} [componentName] - Optional name of the component to be displayed in the dialog, defaults to "ObjectDetails".
 * @param {any} onClickDeleteRwoVersion - Function to handle the delete action.
 * @returns {JSX.Element} A JSX Box containing the action buttons.
 */
export const tableRowActions = (row: any, dispatch: any, componentName?: string, onClickDeleteRwoVersion?: any): JSX.Element => {
  const type: string = row.original.type;
  const name: string = row.original.name;
  const id: string = row.original.id;
  const isCustomObject: boolean = type === "Custom";
  let url = `${objectDetails}?id=${id}`;

  // Determines the URL attached to View Details button based on the component name.
  componentName = componentName ?? "ObjectDetails";
  let dialogComponentName = componentNameToDialogComponentMap[componentName as keyof typeof componentNameToDialogComponentMap];
  if (componentName !== "ObjectDetails") {
    url = `${dialogComponentName?.toLowerCase()}?id=${id}`;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap'
      }}
    >
      <Tooltip title={"View Details"} arrow>
        <a
          onClick={(e) => {
            e.preventDefault();
            const component: IComponent = {
              'params': {id},
              'name': dialogComponentName
            };
            dispatch(setGlobalDialogComponent(component));
            dispatch(setIsGlobalDialogOpen(true));
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            dispatch(setGlobalRightClickMenuData({url}));
            dispatch(setGlobalRightClickMenuPosition({x: e.pageX, y: e.pageY}));
          }}
          href={url}
          target={'_blank'}
        >
          <IconButton size="large">
            {/* <VisibilityIcon fontSize="medium" style={{ color: tableButtonGray }} /> */}
            <ViewDetailsIcon fontSize="medium" style={{color: tableButtonGray}}/>
          </IconButton>
        </a>
      </Tooltip>
      {!isCustomObject && row.original.links?.map((link: Link) => (
        <Tooltip title={`Open ${link.name} Link`} arrow key={link.url}>
          <IconButton
            onClick={() => window.open(`${link.url}`, '_blank')}
            size="large"
          >
            <ArgosLinkIcon fontSize="medium" style={{color: tableButtonGray}}/>
          </IconButton>
        </Tooltip>
      ))}
      <Tooltip title={"Delete Object"} arrow>
        <IconButton
          onClick={(e) => {
            e.preventDefault();
            if (onClickDeleteRwoVersion) {
              onClickDeleteRwoVersion(id);
            }
          }}
          size="large"
        >
          <DeleteIcon fontSize="medium"/>
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export {
  objectDefinitionsTitle,
  objectDetails,
  versionsTabUrlParam,
  fetchObjectsEndpoint,
  fetchJamaMetadataEndpoint,
  deleteObjectsEndpoint,
  reloadObjectsEndpoint,
  addObjectEndpoint,
  updateObjectEndpoint,
  generateUrlEndpoint,
  potentiallyMovingObjectsTitle,
  objectClassificationTitle,
  picklistDataTitle,
  signObjectsTitle,
  miscellaneousObjectsTitle,
  fetchRealWorldObjectsEndpoint,
  addRealWorldObjectImageEndpoint,
  fetchRealWorldObjectsPdfEndpoint,
  savePicklistEndpoint
}